/** Page-level keyboard shortcuts of a record editor (plan §1.5).
 *
 * - `⌘Z` / `Ctrl+Z` undo; `⇧⌘Z`, `⌘Y` (and the Ctrl forms) redo — skipped while focus is
 *   in a text field, where the browser's native undo of the field keeps working.
 * - `⌘S` / `Ctrl+S` save — also from inside text fields (the `EditorSaveBar` precedent).
 *   Pass `onSave` only while saving is possible, so a blocked Save is not walked past.
 * - `E` edit, `Esc` close, `1`–`5` stages — plain keys, outside text fields; a page with
 *   more targets numbers them `1`–`9`, then `0` for a tenth (`stageCount`).
 *
 * Nothing fires inside an open dialog, menu or listbox (their keys are their own), while an
 * IME is composing, or for an event another handler already took (`defaultPrevented`).
 * A shortcut whose handler is absent leaves the event alone. Handlers may change on every
 * render; the listener reads the latest.
 */
import { useEffect, useRef } from 'react';

export interface RecordShortcutHandlers {
  onUndo?: () => void;
  onRedo?: () => void;
  onSave?: () => void;
  onEdit?: () => void;
  onClose?: () => void;
  /** 0-based stage index (`1` → 0, `0` → 9). */
  onStage?: (index: number) => void;
}

export interface RecordShortcutOptions {
  /** Default true. */
  enabled?: boolean;
  /** How many number keys select a stage (default 5). */
  stageCount?: number;
}

/** Input types without a text caret — a focused switch or button does not own ⌘Z. */
const NON_TEXT_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

/** Focus is somewhere typing happens: a text-like input, a textarea, a select, or
 *  contentEditable content. */
export function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  // jsdom does not implement isContentEditable — the attribute lookup covers it.
  if (
    target.isContentEditable ||
    target.closest(
      '[contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]',
    )
  ) {
    return true;
  }
  if (target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return true;
  if (target.tagName === 'INPUT') {
    return !NON_TEXT_INPUT_TYPES.has((target as HTMLInputElement).type);
  }
  return false;
}

const OVERLAY_SELECTOR = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]';

function inOverlay(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(OVERLAY_SELECTOR) !== null;
}

export function useRecordShortcuts(
  handlers: RecordShortcutHandlers,
  { enabled = true, stageCount = 5 }: RecordShortcutOptions = {},
): void {
  const ref = useRef(handlers);
  useEffect(() => {
    ref.current = handlers;
  });

  useEffect(() => {
    if (!enabled) return undefined;
    const run = (e: KeyboardEvent, fn: (() => void) | undefined) => {
      if (!fn) return;
      e.preventDefault();
      fn();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing || inOverlay(e.target)) return;
      const h = ref.current;
      const key = e.key.toLowerCase();
      const typing = isTextEntryTarget(e.target);

      if ((e.metaKey || e.ctrlKey) && !e.altKey) {
        if (key === 's' && !e.shiftKey) return run(e, h.onSave);
        if (typing) return;
        if (key === 'z') return run(e, e.shiftKey ? h.onRedo : h.onUndo);
        if (key === 'y' && !e.shiftKey) return run(e, h.onRedo);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || typing) return;
      if (e.key === 'Escape') return run(e, h.onClose);
      if (e.shiftKey || e.repeat) return;
      if (key === 'e') return run(e, h.onEdit);
      if (/^[0-9]$/.test(e.key)) {
        const index = e.key === '0' ? 9 : Number(e.key) - 1;
        const onStage = h.onStage;
        if (index < stageCount && onStage) run(e, () => onStage(index));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, stageCount]);
}
